import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReferralTracker = () => {
  const [referralCode, setReferralCode] = useState('SARAH2026');
  const [referrals, setReferrals] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    // Mock data - replace with actual Supabase queries
    const mockReferrals = [
      {
        id: 1,
        name: 'Emily Rodriguez',
        status: 'active',
        joinDate: '2026-01-10',
        pointsEarned: 100,
        bonusEarned: 50
      },
      {
        id: 2,
        name: 'Michael Chen',
        status: 'active',
        joinDate: '2026-01-15',
        pointsEarned: 100,
        bonusEarned: 50
      },
      {
        id: 3,
        name: 'Jessica Williams',
        status: 'pending',
        joinDate: '2026-01-22',
        pointsEarned: 0,
        bonusEarned: 0
      }
    ];
    setReferrals(mockReferrals);
  };

  const handleCopyCode = () => {
    navigator?.clipboard?.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform) => {
    const message = `Join me at TrainaryGym! Use my referral code ${referralCode} to get started.`;
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window?.location?.origin)}&quote=${encodeURIComponent(message)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
      email: `mailto:?subject=Join TrainaryGym&body=${encodeURIComponent(message)}`
    };
    window?.open(urls?.[platform], '_blank');
  };

  const totalPointsEarned = referrals?.reduce((sum, ref) => sum + ref?.pointsEarned + ref?.bonusEarned, 0);
  const activeReferrals = referrals?.filter(r => r?.status === 'active')?.length;
  const pendingReferrals = referrals?.filter(r => r?.status === 'pending')?.length;

  return (
    <div className="space-y-6">
      {/* Referral Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-success/20 flex items-center justify-center">
              <Icon name="Users" size={24} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Referrals</p>
              <h3 className="text-2xl font-heading font-bold text-foreground">
                {activeReferrals}
              </h3>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-warning/20 flex items-center justify-center">
              <Icon name="Clock" size={24} color="var(--color-warning)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending</p>
              <h3 className="text-2xl font-heading font-bold text-foreground">
                {pendingReferrals}
              </h3>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon name="Coins" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Points Earned</p>
              <h3 className="text-2xl font-heading font-bold text-foreground">
                {totalPointsEarned}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Referral Code Section */}
      <div className="card">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Your Referral Code
        </h2>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="flex gap-2">
              <Input
                value={referralCode}
                readOnly
                className="font-mono text-lg font-semibold"
              />
              <Button
                variant={copied ? 'success' : 'default'}
                onClick={handleCopyCode}
                iconName={copied ? 'Check' : 'Copy'}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Share this code with friends. You'll earn 100 points when they join!
            </p>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-sm font-medium text-foreground mb-3">Share via:</p>
          <div className="flex gap-3 flex-wrap">
            <Button
              variant="outline"
              onClick={() => handleShare('facebook')}
              iconName="Facebook"
            >
              Facebook
            </Button>
            <Button
              variant="outline"
              onClick={() => handleShare('twitter')}
              iconName="Twitter"
            >
              Twitter
            </Button>
            <Button
              variant="outline"
              onClick={() => handleShare('whatsapp')}
              iconName="MessageCircle"
            >
              WhatsApp
            </Button>
            <Button
              variant="outline"
              onClick={() => handleShare('email')}
              iconName="Mail"
            >
              Email
            </Button>
          </div>
        </div>
      </div>

      {/* Referral History */}
      <div className="card">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Referral History
        </h2>
        {referrals?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <p className="text-muted-foreground">No referrals yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Start sharing your code to earn rewards!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {referrals?.map((referral) => (
              <div
                key={referral?.id}
                className="p-4 rounded-lg border border-border hover:border-border/60 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-sm font-semibold text-primary">
                        {referral?.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{referral?.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        Joined {new Date(referral?.joinDate)?.toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 justify-end mb-1">
                      <Icon name="Coins" size={14} color="var(--color-primary)" />
                      <span className="text-sm font-semibold text-primary">
                        +{referral?.pointsEarned + referral?.bonusEarned} pts
                      </span>
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        referral?.status === 'active' ?'bg-success/20 text-success' :'bg-warning/20 text-warning'
                      }`}
                    >
                      {referral?.status === 'active' ? 'Active' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Referral Program Details */}
      <div className="card bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/20">
        <h2 className="text-xl font-heading font-semibold text-foreground mb-4">
          Referral Rewards Program
        </h2>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Icon name="Gift" size={20} color="var(--color-primary)" className="mt-0.5" />
            <div>
              <p className="font-medium text-foreground">100 Points per Referral</p>
              <p className="text-sm text-muted-foreground">
                Earn 100 points when your friend joins and completes their first workout
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Star" size={20} color="var(--color-accent)" className="mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Bonus Rewards</p>
              <p className="text-sm text-muted-foreground">
                Refer 5 friends and get an extra 250 bonus points!
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Users" size={20} color="var(--color-success)" className="mt-0.5" />
            <div>
              <p className="font-medium text-foreground">Your Friend Gets Rewards Too</p>
              <p className="text-sm text-muted-foreground">
                They'll receive 50 welcome points when they sign up with your code
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralTracker;